import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  listExerciseProgrammes,
  createExerciseProgramme,
  emailExerciseProgramme,
} from "@/lib/exercise-programmes.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Dumbbell, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/exercise-programmes")({
  component: ExerciseProgrammesPage,
});

function ExerciseProgrammesPage() {
  const list = useServerFn(listExerciseProgrammes);
  const create = useServerFn(createExerciseProgramme);
  const email = useServerFn(emailExerciseProgramme);
  const qc = useQueryClient();

  const [patientName, setPatientName] = useState("");
  const [patientEmail, setPatientEmail] = useState("");
  const [programmeUrl, setProgrammeUrl] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailingId, setEmailingId] = useState<string | null>(null);

  const { data: programmes = [], isPending } = useQuery({
    queryKey: ["admin", "exercise-programmes"],
    queryFn: () => list(),
  });

  async function saveAndEmail(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const { id } = await create({
        data: { patientName, patientEmail, programmeUrl, accessCode, notes },
      });
      const result = await email({ data: { id } });
      toast.success(
        result.copySent
          ? `Programme emailed to ${patientEmail} — copy sent to you.`
          : `Programme emailed to ${patientEmail}.`,
      );
      setPatientName("");
      setPatientEmail("");
      setProgrammeUrl("");
      setAccessCode("");
      setNotes("");
      qc.invalidateQueries({ queryKey: ["admin", "exercise-programmes"] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function resend(id: string, to: string) {
    setEmailingId(id);
    try {
      await email({ data: { id } });
      toast.success(`Resent to ${to}.`);
      qc.invalidateQueries({ queryKey: ["admin", "exercise-programmes"] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setEmailingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Exercise Programmes</h1>
        <p className="text-sm text-muted-foreground">
          Paste a patient's exercise programme link and access code, and email it to them.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Dumbbell className="h-4 w-4" /> New exercise programme
          </CardTitle>
          <CardDescription>The patient receives an email with the link and code.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveAndEmail} className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ep-name">Patient name *</Label>
              <Input
                id="ep-name"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                required
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ep-email">Patient email *</Label>
              <Input
                id="ep-email"
                type="email"
                value={patientEmail}
                onChange={(e) => setPatientEmail(e.target.value)}
                required
                maxLength={255}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ep-url">Programme link *</Label>
              <Input
                id="ep-url"
                type="url"
                placeholder="https://…"
                value={programmeUrl}
                onChange={(e) => setProgrammeUrl(e.target.value)}
                required
                maxLength={1000}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ep-code">Access code</Label>
              <Input
                id="ep-code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ep-notes">Notes for the patient (optional)</Label>
              <Textarea
                id="ep-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="e.g. Do these twice a day, stop if sharp pain."
              />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 h-4 w-4" />
                )}
                Save & email to patient
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Previously sent</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isPending ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : programmes.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No programmes sent yet.</p>
          ) : (
            <ul className="divide-y">
              {programmes.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{p.patient_name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {p.patient_email} · {new Date(p.created_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                    <a
                      href={p.programme_url}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate text-xs text-primary underline"
                    >
                      {p.programme_url}
                    </a>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {p.emailed_at && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Emailed
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={emailingId === p.id}
                      onClick={() => resend(p.id, p.patient_email)}
                    >
                      {emailingId === p.id ? (
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Mail className="mr-2 h-3.5 w-3.5" />
                      )}
                      Resend
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
